import React, { useState } from 'react';
import { Icons } from '../../../../assets/icons';
import Close from '@mui/icons-material/Close';
import { ReactComponent as EditIcon } from '../../../../assets/icons/ic_edit.svg';
import { type components } from '../../../../endpoint/Schema';
import FAQEditQuestion from './FAQEditQuestion';
import OpenAPI from '../../../../endpoint/Openapi';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import useFAQStore from '../../../../stores/FAQStore';
import {
  InputSwitch,
  type InputSwitchChangeEvent,
} from 'primereact/inputswitch';
import { type CustomError } from '../../../../models/Error';
import { Toast } from 'primereact/toast';

export default function FAQQuestion(props: components['schemas']['FaqDto']) {
  const tenantId = useTenantStore(selectTenantId);
  const { disableFAQ } = useFAQStore();
  const { deleteFAQ } = useFAQStore();
  const [editing, setEditing] = useState(false);
  const toast = React.useRef<Toast>(null);
  function onEdit() {
    setEditing(true);
  }

  function onCancelEditing() {
    setEditing(false);
  }

  async function onDisable(event: InputSwitchChangeEvent) {
    if (!props.id) return;

    try {
      disableFAQ(props.topicId, props.id);

      await OpenAPI.PUT('/api/v2/admin/faq/{faqId}/status', {
        params: {
          path: { faqId: props.id },
          query: { tenantId },
        },
        body: {
          enabled: event.value ?? false,
        },
      });
    } catch {}
  }

  async function onDelete() {
    if (!props.id) return;

    try {
      deleteFAQ(props.topicId, props.id);

      await OpenAPI.DELETE('/api/v2/admin/faq/{faqId}', {
        params: {
          path: { faqId: props.id },
          query: { tenantId },
        },
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }
  }

  if (editing) {
    return (
      <FAQEditQuestion
        faq={props}
        onSave={onCancelEditing}
        onCancel={onCancelEditing}
      />
    );
  }

  return (
    <div
      className={`overflow-hidden transition-[max-height] px-2 bg-g10 text-2xl duration-300 rounded-[1.25rem] shadow-[0px_4px_12px_rgba(0,0,0,0.1)] ${
        !props.enabled ? 'bg-ghostWhite' : ''
      }`}
    >
      <div className="py-6">
        <div className="flex flex-col gap-1 px-8">
          <div className="flex w-full items-center">
            <div className="flex flex-1 gap-4 items-center">
              <img src={Icons.ic_question} className="w-10" />
              Question
            </div>
            <div className="flex flex-row items-center gap-2">
              <InputSwitch
                checked={props.enabled ?? false}
                onChange={onDisable}
                disabled={false}
              />
            </div>
            <div className="flex flex-row items-center gap-2">
              <button onClick={onEdit}>
                <EditIcon
                  height="1.75rem"
                  className="aspect-square fill-primary stroke-primary hover:fill-primaryDark"
                />
              </button>
              <button onClick={onDelete}>
                <Close
                  sx={{ fontSize: '1.5rem' }}
                  className="aspect-square stroke-primary hover:stroke-primaryDark"
                />
              </button>
            </div>
          </div>

          {props.question}
        </div>

        <div className="flex flex-col gap-1 px-8 py-8 mt-4">
          <div className="flex  gap-4 items-center font-medium">
            <img src={Icons.ic_answer} className="w-10" />
            Answer
          </div>

          {props.answer}
        </div>
      </div>
      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}
